import React, { useState, useEffect, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormikContext } from "formik";
import { Box, Text } from "@chakra-ui/react";

import { FullPageSpinner } from "components/Spinner";
import MultiPaymentMethods from "components/MultiPaymentMethods";
import AddPlaidACHButton from "containers/Pages/PlaidACH/AddPlaidACHButton";
import { getFinalPaymentMethods } from "components/PaymentMethods";
import { CurrencyInput } from "components/Form";

import { useGetTransactionMethods } from "api/transaction-methods";

const SelectPaymentMethod = ({ onSubmit, chooserConfig }) => {
  const { formatMessage } = useIntl();
  const transactionMethods = useGetTransactionMethods();
  const { setFieldValue, values } = useFormikContext();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [depositPMs, setDepositPMs] = useState([]);

  const refetch = transactionMethods.refetch;
  const { addACH } = chooserConfig;

  const handlePaymentMethodUpdated = useCallback(async () => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (transactionMethods.data) {
      const pmsDict = transactionMethods.data.payload.payment_methods;
      let paymentMethods = Object.keys(pmsDict).map((k) => pmsDict[k]);
      let depositPMs = getFinalPaymentMethods(paymentMethods, chooserConfig);

      setPaymentMethods(paymentMethods);
      setDepositPMs(depositPMs);
    }
  }, [transactionMethods.data, setDepositPMs, setPaymentMethods, chooserConfig]);

  const formatPaymentMethod = (paymentMethod) => {
    return paymentMethod ? [paymentMethod] : [];
  };

  const handleSubmit = () => {
    // get the wallet and set it to form field
    // this is done under the hood because deposits can only go to USD for now
    // future case we may allow other types of wallets to send funds to
    const wallet = paymentMethods.find(
      (pm) => pm.provider === "wallet" && pm.processing_currency === "USD"
    );
    const depositPaymentMethodId = values.paymentMethod.payment_method_id;
    const paymentMethod = depositPMs.find((pm) => pm.id === depositPaymentMethodId);

    setFieldValue("destinationWalletId", wallet.provider_data.wallet_id);
    setFieldValue("paymentMethod", paymentMethod);
    setFieldValue("depositPaymentMethodId", depositPaymentMethodId);

    const requestAmount = values.requestAmount;
    const formattedValues = { depositPaymentMethodId, requestAmount };

    onSubmit(formattedValues);
  };

  if (transactionMethods.isLoading) {
    return <FullPageSpinner data-testid="deposit-select-payment-method" />;
  }

  return (
    <Box>
      <Box mb="3">
        <Text fontWeight="bold" color="primaryText">
          <FormattedMessage
            defaultMessage="How much do you want to load?"
            description="Inquiry message"
          />
        </Text>
      </Box>
      <Box>
        <CurrencyInput currency="USD" name="requestAmount" />
        <Text fontSize="12">
          <FormattedMessage
            defaultMessage="Funds will be added to your USD wallet"
            description="Informative message"
          />
        </Text>
      </Box>
      <Box mt="8" mb="8">
        <Text fontWeight="bold" color="primaryText">
          <FormattedMessage
            defaultMessage="Choose your payment method"
            description="Description for user action"
          />
        </Text>
        <Text fontSize="12">
          <FormattedMessage
            defaultMessage="A fee will be assessed on top of the requested amount"
            description="Informative message"
          />
        </Text>
        <MultiPaymentMethods
          submitLabel={formatMessage({
            defaultMessage: "Use this payment method",
            description: "Button label",
          })}
          chooserConfig={chooserConfig}
          paymentMethods={depositPMs}
          selectedPaymentMethods={formatPaymentMethod(values.paymentMethod)}
          setSelectedPaymentMethods={(cb) =>
            setFieldValue("paymentMethod", cb(formatPaymentMethod(values.paymentMethod))[0])
          }
          submitIsDisabled={!values.requestAmount || !values.paymentMethod}
          onSubmit={handleSubmit}
          onPaymentMethodUpdated={handlePaymentMethodUpdated}
        />
      </Box>
      {addACH && (
        <Box>
          <Text mb="1">
            <FormattedMessage
              defaultMessage="Add a payment method to your account"
              description="Description for user action"
            />
          </Text>
          <AddPlaidACHButton />
        </Box>
      )}
    </Box>
  );
};

export default SelectPaymentMethod;
